import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const stats = [
  {
    id: 1,
    value: 4,
    suffix: '+',
    label: 'Years Experience',
    description: 'Trusted expertise'
  },
  {
    id: 2,
    value: 1000,
    suffix: '+',
    label: 'Cars Repaired',
    description: 'Satisfied vehicles'
  },
  {
    id: 3,
    value: 500,
    suffix: '+',
    label: 'Happy Customers',
    description: 'Five star reviews'
  },
  {
    id: 4,
    value: 24,
    suffix: '/7',
    label: 'Availability',
    description: 'Always open'
  }
];

const AnimatedCounter = ({ value, suffix, isVisible }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!isVisible) return;
    
    let startTime;
    const duration = 2000; // 2 seconds
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * value));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [value, isVisible]);
  
  return (
    <span className="stat-number">
      {count}{suffix}
    </span>
  );
};

const StatCard = ({ stat, isVisible }) => {
  return (
    <div 
      className="stat-card relative text-center p-8 lg:p-10"
      data-testid={`stat-card-${stat.id}`}
    >
      {/* Background glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent rounded-2xl"></div>
      
      {/* Content */}
      <div className="relative z-10">
        {/* Number */}
        <div className="font-orbitron text-5xl sm:text-6xl lg:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-white mb-4">
          <AnimatedCounter value={stat.value} suffix={stat.suffix} isVisible={isVisible} />
        </div>
        
        {/* Label */}
        <h3 className="font-orbitron text-lg sm:text-xl font-bold text-white uppercase tracking-wider mb-2">
          {stat.label}
        </h3>
        
        {/* Description */}
        <p className="text-gray-500 text-sm">
          {stat.description}
        </p>
      </div>
      
      {/* Decorative border */}
      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-gradient-to-r from-transparent via-cyan-500 to-transparent rounded-full"></div>
    </div>
  );
};

export default function StatsSection() {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: 'top 70%',
        onEnter: () => setIsVisible(true),
      });
      
      // Animate stat cards
      gsap.fromTo(
        '.stat-card',
        { opacity: 0, y: 50, scale: 0.9 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          stagger: 0.15,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section 
      ref={sectionRef}
      id="stats" 
      className="relative py-20 lg:py-28 overflow-hidden"
      data-testid="stats-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-[#0A0A0A]"></div>
      
      {/* Decorative grid lines */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"></div>
        <div className="absolute left-1/4 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-cyan-500/30 to-transparent hidden lg:block"></div>
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-cyan-500/30 to-transparent"></div>
        <div className="absolute left-3/4 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-cyan-500/30 to-transparent hidden lg:block"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8">
          {stats.map((stat) => (
            <StatCard key={stat.id} stat={stat} isVisible={isVisible} />
          ))}
        </div>
      </div>
    </section>
  );
}
